/*!
 * IOAN tracking capture v1.1.2
 * Consent-aware capture of incoming UTM / Triple Whale identifiers into PRIVATE
 * Shopify cart attributes, so they land on the resulting order's
 * "Additional details" in the Shopify admin.
 *
 * Owner:      Industry of All Nations - marketing ops
 * Namespace:  __ioan_tracking_*   (double underscore = private cart attribute;
 *             not readable from Liquid or the Ajax API, visible on the order
 *             in the Shopify admin. Ref: shopify.dev/docs/api/ajax/reference/cart)
 * Rule:       last eligible tagged landing for the current cart.
 *
 * What is stored, exactly:
 *   One sessionStorage key, "ioan_tracking_sig", whose value is
 *       "v3:" + fnv1a32(cartId + "|" + signature)
 *   - cartId    = the cart token as returned in the /cart/update.js response of
 *                 the write being recorded, with anything from "?" onward removed
 *                 (the "?key=..." secret is never kept). No cookie is ever read.
 *   - signature = JSON.stringify of the ordered [field, value] pairs that were
 *                 accepted (unambiguous: delimiter characters inside values
 *                 cannot collide with field boundaries).
 *   The value is a 32-bit non-reversible digest (8 hex chars). It does not
 *   contain the campaign values or the cart token in plaintext. It is NOT a
 *   cryptographic guarantee; it is a dedupe key. It is written only after a
 *   consented cart write returned 2xx AND the response identified the cart that
 *   was written. If the cart identity is unknown (GET /cart.js failed, or the
 *   write response carried no token) nothing is persisted; that unidentified
 *   write is remembered in page memory only, and a later capture on the same
 *   page is always checked against a freshly read cart identity.
 *   The previous key is discarded the moment a new capture write is issued, so
 *   an uncertain or unidentified write can never leave stale proof behind.
 *   Before consent nothing is stored anywhere and no cart request is made.
 *
 * Write budget per page load:
 *   up to 3 capture writes and 3 cleanup writes. A capture is only issued while
 *   at least one cleanup write is still available, so a successful capture can
 *   always be withdrawn on the same page as long as the network cooperates.
 *
 * Cleanup limitation (stated, not guaranteed):
 *   On withdrawal this script issues ONE blanking request for its own namespace
 *   (queued behind any in-flight request). If that request fails, values that
 *   already reached the cart remain until a later successful cleanup or a new
 *   consented capture replaces them. There is deliberately no retry loop.
 *   Consent is re-checked at every write boundary, not only on the
 *   visitorConsentCollected event; a denial visible to the Customer Privacy API
 *   when a capture completes triggers one bounded cleanup whether or not its
 *   event has arrived. A withdrawal that is neither reported by the API nor
 *   delivered as an event cannot be acted on.
 *
 * Deliberately does NOT: wrap fetch/XHR, listen to cart or Rebuy events,
 * rewrite links, read cookies, use localStorage, or block checkout.
 * A failed capture must never prevent a purchase.
 */
(function () {
  "use strict";

  if (window.__ioanTrackingCaptureLoaded) return;
  window.__ioanTrackingCaptureLoaded = true;

  var NS = "__ioan_tracking_";
  var VERSION = "1.1.2";
  var RULE = "last_tagged_landing";
  var FIELDS = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
    "tw_source",
    "tw_adid"
  ];
  var META = ["rule", "version", "captured_at", "rejected"];
  var MAX_LEN = 128;
  var MAX_CAPTURE_WRITES = 3; // capture POSTs per page load
  var MAX_CLEANUP_WRITES = 3; // cleanup POSTs per page load; a capture needs a free slot here
  var SIG_KEY = "ioan_tracking_sig"; // sessionStorage; see header for contents
  var SIG_PREFIX = "v3:"; // bumped whenever the hashed representation changes

  var captureWrites = 0;
  var cleanupWrites = 0;
  var inFlight = false;
  var inFlightKind = null; // "capture" | "cleanup" while inFlight
  var queued = null; // "capture" | "cleanup" | null - one action waits behind an in-flight request
  var pending = null; // snapshot held in memory only until consent is confirmed
  var epoch = 0; // bumped on every consent change; async results from an older epoch never write state
  var dirty = false; // true from the moment a capture POST is issued until a cleanup succeeds
  var lastWrite = null; // { sig, cartId } of the last successful write on this page; cartId "" = unidentified

  // --------------------------------------------------------------- sanitising

  // Values arrive already decoded once by URLSearchParams. Do not decode again.
  function clean(raw) {
    if (typeof raw !== "string") return null;
    var v = raw.replace(/[\x00-\x1F\x7F]/g, "").trim();
    if (!v) return null;
    if (v.length > MAX_LEN) v = v.slice(0, MAX_LEN);
    // Unresolved ad-platform macros must never be stored as identifiers.
    if (/\{\{|\}\}|%7B%7B/i.test(v)) return null; // e.g. {{ad.id}}
    if (/^__[A-Za-z0-9_]+__$/.test(v)) return null; // e.g. __CID__
    return v;
  }

  // Format validation only. This proves the shape of an ad ID, NOT its identity.
  function validAdId(v) {
    return /^[0-9]{1,32}$/.test(v) ? v : null;
  }

  function readSnapshot() {
    var params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (e) {
      return null;
    }
    var values = {};
    var rejected = [];
    var present = false;

    for (var i = 0; i < FIELDS.length; i++) {
      var key = FIELDS[i];
      if (!params.has(key)) continue;
      present = true;
      var v = clean(params.get(key));
      if (v === null) {
        rejected.push(key);
        continue;
      }
      if (key === "tw_adid") {
        v = validAdId(v);
        if (v === null) {
          rejected.push(key);
          continue;
        }
      }
      values[key] = v;
    }

    if (!present) return null; // untagged navigation
    if (!Object.keys(values).length) return null; // every field rejected: do not erase
    return { values: values, rejected: rejected };
  }

  // Canonical, unambiguous serialisation: ordered [field, value] pairs as JSON.
  function signature(values) {
    var pairs = [];
    for (var i = 0; i < FIELDS.length; i++) {
      var k = FIELDS[i];
      if (Object.prototype.hasOwnProperty.call(values, k)) pairs.push([k, values[k]]);
    }
    return JSON.stringify(pairs);
  }

  // FNV-1a, 32-bit, hex. A dedupe digest, not a security primitive.
  function fnv1a32(str) {
    var h = 0x811c9dc5;
    for (var i = 0; i < str.length; i++) {
      h ^= str.charCodeAt(i);
      h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
    }
    return ("0000000" + h.toString(16)).slice(-8);
  }

  // Cart token with any "?key=..." (or other) suffix removed. Never persisted raw.
  // Returns "" when the identity is unknown.
  function cartIdFrom(cart) {
    try {
      if (cart && typeof cart.token === "string") return cart.token.split("?")[0];
    } catch (e) {
      /* fall through */
    }
    return "";
  }

  function dedupeKey(cartId, sig) {
    return SIG_PREFIX + fnv1a32(cartId + "|" + sig);
  }

  // ----------------------------------------------------------------- consent

  // Conservative: attribution capture requires BOTH marketing and analytics
  // permission. Fails closed when the API is missing, not ready, or throws.
  function consentGranted() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (!cp) return false;
      if (typeof cp.marketingAllowed !== "function") return false;
      if (typeof cp.analyticsProcessingAllowed !== "function") return false;
      return cp.marketingAllowed() === true && cp.analyticsProcessingAllowed() === true;
    } catch (e) {
      return false;
    }
  }

  function withPrivacyApi(cb) {
    try {
      var S = window.Shopify;
      if (S && S.customerPrivacy && typeof S.customerPrivacy.marketingAllowed === "function") {
        cb();
        return;
      }
      if (S && typeof S.loadFeatures === "function") {
        S.loadFeatures([{ name: "consent-tracking-api", version: "0.1" }], function (err) {
          if (err) return; // fail closed
          try {
            cb();
          } catch (e) {
            /* never throw into Shopify's callback */
          }
        });
      }
      // No API available at all: fail closed, capture nothing.
    } catch (e) {
      /* fail closed */
    }
  }

  // ------------------------------------------------------------------ storage

  function readSig() {
    try {
      return window.sessionStorage.getItem(SIG_KEY);
    } catch (e) {
      return null;
    }
  }
  function writeSig(v) {
    try {
      window.sessionStorage.setItem(SIG_KEY, v);
    } catch (e) {
      /* private mode / blocked storage: dedupe degrades, capture still works */
    }
  }
  function dropSig() {
    try {
      window.sessionStorage.removeItem(SIG_KEY);
    } catch (e) {
      /* ignore */
    }
  }

  // Forget every dedupe record (persisted and in-memory).
  function forgetCaptured() {
    dropSig();
    lastWrite = null;
  }

  // ---------------------------------------------------------------- requests

  function root() {
    try {
      if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
        return window.Shopify.routes.root; // locale-aware
      }
    } catch (e) {
      /* keep default */
    }
    return "/";
  }

  // cb(ok, jsonOrNull) is called exactly once and never throws outward.
  function request(method, path, body, cb) {
    var done = false;
    function finish(ok, json) {
      if (done) return;
      done = true;
      try {
        cb(ok, json);
      } catch (e) {
        /* never break the storefront */
      }
    }
    try {
      var opts = { method: method, headers: { Accept: "application/json" } };
      if (body !== undefined) {
        opts.headers["Content-Type"] = "application/json";
        opts.body = JSON.stringify(body);
      }
      window
        .fetch(root() + path, opts)
        .then(function (res) {
          var ok = !!(res && res.ok);
          var p = null;
          try {
            p = res.json();
          } catch (e) {
            p = null;
          }
          if (p && typeof p.then === "function") {
            p.then(
              function (j) {
                finish(ok, j);
              },
              function () {
                finish(ok, null);
              }
            );
          } else {
            finish(ok, null);
          }
        })
        .catch(function () {
          finish(false, null); // silent: never surface to the shopper, never retry-storm
        });
    } catch (e) {
      finish(false, null);
    }
  }

  // ---------------------------------------------------------------- payloads

  // Always writes the COMPLETE namespace so a new campaign replaces the previous
  // snapshot as one coherent set and no stale field survives from another visit.
  // Absent fields are sent as "" rather than omitted. Sends ONLY this namespace;
  // Shopify merges attributes, so unrelated cart state is untouched.
  function buildAttributes(values, rejected) {
    var attrs = {};
    for (var i = 0; i < FIELDS.length; i++) {
      var k = FIELDS[i];
      attrs[NS + k] = Object.prototype.hasOwnProperty.call(values, k) ? values[k] : "";
    }
    attrs[NS + "rule"] = RULE;
    attrs[NS + "version"] = VERSION;
    attrs[NS + "captured_at"] = new Date().toISOString();
    attrs[NS + "rejected"] = rejected && rejected.length ? rejected.join(",") : "";
    return attrs;
  }

  function blankAttributes() {
    var attrs = {};
    var i;
    for (i = 0; i < FIELDS.length; i++) attrs[NS + FIELDS[i]] = "";
    for (i = 0; i < META.length; i++) attrs[NS + META[i]] = "";
    return attrs;
  }

  // --------------------------------------------------------------------- flow

  function runQueued() {
    var q = queued;
    queued = null;
    if (q === "capture") attemptCapture();
    else if (q === "cleanup") attemptCleanup();
  }

  function cleanupSlotAvailable() {
    return cleanupWrites < MAX_CLEANUP_WRITES;
  }

  // Consent has gone away (event or API) after a capture may have reached the
  // cart: forget dedupe state and issue one bounded cleanup.
  function withdraw() {
    var mayHaveWritten = dirty || readSig() !== null;
    forgetCaptured();
    if (queued === "capture") queued = null;
    if (!mayHaveWritten) return;
    // One cleanup is enough: do not stack another behind one already queued or in flight.
    if (queued === "cleanup") return;
    if (inFlight && inFlightKind === "cleanup") return;
    attemptCleanup();
  }

  // GET /cart.js (only after consent, only with a tagged snapshot) to learn the
  // current cart, compare the dedupe key, then POST the full namespace.
  function attemptCapture() {
    if (!pending) return;
    if (inFlight) {
      queued = "capture";
      return;
    }
    if (captureWrites >= MAX_CAPTURE_WRITES) return;
    if (!cleanupSlotAvailable()) return; // never write what could not be withdrawn
    if (!consentGranted()) return;

    var myEpoch = epoch;
    var snapshot = pending;
    var sig = signature(snapshot.values);
    // Page-only fallback, limited to an UNIDENTIFIED write of this same snapshot:
    // an unknown cart cannot be compared with anything, so do not write again.
    // An identified write is always re-checked against a fresh cart identity.
    if (lastWrite && lastWrite.sig === sig && lastWrite.cartId === "") return;

    inFlight = true;
    inFlightKind = "capture";
    request("GET", "cart.js", undefined, function (ok, cart) {
      // Write boundary: re-check consent itself, not only the event epoch.
      if (myEpoch !== epoch || !consentGranted()) {
        inFlight = false;
        runQueued();
        return;
      }
      var cartId = ok ? cartIdFrom(cart) : "";
      // Only a KNOWN identity may be compared against the persisted key. An
      // unknown identity must never suppress a write on what may be another cart.
      var seenInMemory = lastWrite !== null && lastWrite.sig === sig && lastWrite.cartId === cartId;
      if (cartId && (seenInMemory || readSig() === dedupeKey(cartId, sig))) {
        inFlight = false; // already captured this snapshot on THIS cart
        runQueued();
        return;
      }
      if (captureWrites >= MAX_CAPTURE_WRITES || !cleanupSlotAvailable()) {
        inFlight = false;
        runQueued();
        return;
      }

      captureWrites++;
      dirty = true;
      // Invalidate the previous proof NOW: the snapshot on the cart is about to
      // change, and an uncertain or unidentified outcome must not leave a key
      // claiming the old snapshot is still current.
      dropSig();
      lastWrite = null;
      request("POST", "cart/update.js", { attributes: buildAttributes(snapshot.values, snapshot.rejected) }, function (ok2, updated) {
        inFlight = false;
        var grantedNow = consentGranted(); // actual permission, independent of event delivery
        if (ok2 && grantedNow) {
          // Remember what was actually written (page memory). Persist only when
          // the response names the written cart AND no consent event moved the
          // epoch mid-flight; the pre-write GET token is never used as proof.
          var writtenId = cartIdFrom(updated);
          lastWrite = { sig: sig, cartId: writtenId };
          if (writtenId && myEpoch === epoch) writeSig(dedupeKey(writtenId, sig));
        } else if (!grantedNow) {
          // Permission is gone at the write boundary, whether or not its event
          // has arrived and whether or not the epoch moved: forget proof and run
          // ONE cleanup. Going through the queue merges with any cleanup the
          // event handler already queued instead of adding a second one.
          forgetCaptured();
          queued = "cleanup";
        }
        runQueued();
      });
    });
  }

  function attemptCleanup() {
    if (inFlight) {
      queued = "cleanup";
      return;
    }
    if (!cleanupSlotAvailable()) return;
    cleanupWrites++;
    inFlight = true;
    inFlightKind = "cleanup";
    request("POST", "cart/update.js", { attributes: blankAttributes() }, function (ok) {
      inFlight = false;
      if (ok) dirty = false;
      // !ok: values may remain on the cart. Stated limitation; no retry.
      runQueued();
    });
  }

  function onConsentChange() {
    epoch++;
    if (consentGranted()) {
      // Latest decision wins: a cleanup waiting behind an in-flight request is
      // superseded by re-capture (which rewrites the full namespace anyway).
      if (queued === "cleanup") queued = null;
      attemptCapture();
      return;
    }
    withdraw();
  }

  function start() {
    pending = readSnapshot();

    // Listen regardless, so a withdrawal on an untagged page still clears state.
    try {
      document.addEventListener("visitorConsentCollected", onConsentChange);
    } catch (e) {
      /* ignore */
    }

    if (!pending) return;

    withPrivacyApi(function () {
      attemptCapture(); // no-op unless consent is granted
      // Not granted yet: values stay in memory only. If consent arrives on this
      // page, visitorConsentCollected captures them. Losing unconsented campaign
      // data after navigation is the intended, preferable outcome.
    });
  }

  try {
    start();
  } catch (e) {
    /* capture must never break the storefront */
  }
})();
